import {Flex, Icon, Input, noop, PriorityIcon, Text} from '@priority-software/priority-style-react';
import React, {FocusEvent, RefObject, useCallback} from 'react';

import styles from '../styles.module.scss';
import {
    DurationControl,
    NumberControl,
    TextValidationControl,
    TimeControl
} from '../../../../../components/commonOldtoNew';
import {Operator} from '@priority-software/priority-utils/lib/operators';
import {ISearchCondition} from '../../../types/filters';
import {ColumnType} from '../../../types/column';
import {getLang} from '../../../../../Localization';
import {getStringFormatByColumnType} from '../../../../Form/utils/date';
import {getDecimalSign, getThousandSign} from '../../../../../_shared/GWTBridge/invoke/priTextBox';
import {ISearchField} from '../../../../../common/searchType';
import {getLocalizedText, texts} from '../../../../../constants/TextConst';
import {createDataTestId} from '../../../../../utils/dataTestId';

interface Props {
    filter: ISearchCondition;
    column: ISearchField;
    onUpdate: (value: any) => void;
    isDisabled: boolean;
}

type ControlInputProps = {
    autoFocus?: boolean;
    value: string;
    formated?: string;
    handleChange: (value: string | number) => void;
    placeholder?: string;
    disabled?: boolean;
    onBlur: (event?: FocusEvent<HTMLInputElement>) => void;
    inputRef?: RefObject<HTMLInputElement>;
    onFocus: (event?: FocusEvent<HTMLInputElement>) => void;
};

const ControlInput = ({
    autoFocus,
    value,
    formated,
    handleChange,
    placeholder,
    disabled,
    onBlur,
    inputRef,
    onFocus
}: ControlInputProps) => (
    <Input
        inputRef={inputRef}
        direction="ltr"
        onFocus={onFocus}
        value={formated || value}
        autoFocus={!!autoFocus}
        placeholder={placeholder}
        componentClasses={styles.filterInput}
        onChange={event => handleChange(event.target.value)}
        disabled={disabled}
        onBlur={onBlur}
        maxLength={21}
    >
        {value && (
            <div
                onMouseDown={e => {
                    handleChange('');
                }}
            >
                <Icon icon={PriorityIcon.closeSmall} skin="standart" onClick={noop} />
            </div>
        )}
    </Input>
);

const TypeToControlMap = {
    [ColumnType.Duration]: DurationControl,
    [ColumnType.Number]: NumberControl,
    [ColumnType.Time]: TimeControl,
    [ColumnType.Date]: TextValidationControl
};

const RangeControl = ({filter, column, onUpdate, isDisabled}: Props) => {
    const {type, values: filterValues, operator} = filter;
    const isBetween = operator === Operator.Between;

    const onMinValueChange = useCallback(
        minValue => {
            onUpdate(checkForEmptyValues(minValue, filterValues[1]));
        },
        [onUpdate, filterValues]
    );

    const onMaxValueChange = useCallback(
        maxValue => {
            onUpdate(checkForEmptyValues(filterValues[0], maxValue));
        },
        [onUpdate, filterValues]
    );

    const lang = getLang();
    const format = getStringFormatByColumnType(column.type, lang);

    const Comp = TypeToControlMap[type] || TextValidationControl;

    return (
        <Flex
            componentClasses={styles.betweenWrapper}
            justifyContent="flex-start"
            {...createDataTestId('range-control-filter-box', true)}
        >
            {isBetween && (
                <Text wrap={false}>
                    {type === ColumnType.Number
                        ? getLocalizedText(texts.filterMin)
                        : getLocalizedText(texts.from)}
                </Text>
            )}
            <Comp
                {...column}
                format={format}
                autoFocus
                disabled={isDisabled}
                onChange={onMinValueChange}
                decimalSign={getDecimalSign()}
                thousandSign={getThousandSign()}
                value={filterValues[0]}
                render={ControlInput}
            />
            {isBetween && (
                <>
                    <Text wrap={false}>
                        {type === ColumnType.Number
                            ? getLocalizedText(texts.filtersMax)
                            : getLocalizedText(texts.to)}
                    </Text>
                    <Comp
                        {...column}
                        format={format}
                        disabled={isDisabled}
                        decimalSign={getDecimalSign()}
                        thousandSign={getThousandSign()}
                        onChange={onMaxValueChange}
                        value={filterValues[1]}
                        render={ControlInput}
                    />
                </>
            )}
        </Flex>
    );
};

function checkForEmptyValues(first: string = '', second: string = '') {
    return first === '' && second === '' ? [] : [first, second];
}

export default RangeControl;
